import React from 'react';
import { Navigate, useLoaderData } from 'react-router-dom';
import { css } from '@compiled/react';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { ContentOverviewCard, ContentOverviewCardState, ContentType, Heading } from '@atlassian/learning-components';

import { ALM_INTEGRATION_FF } from 'src/constants';
import { isDevelopment } from 'src/services/http/user';

import { CalendarLoaderProps } from './loader';

export const Calendar: React.FC = () => {
  const { calendarEvents } = useLoaderData() as CalendarLoaderProps;

  const almIntegrationEnabled = FeatureGates.checkGate(ALM_INTEGRATION_FF);

  // TODO: remove this once ALM integration is enabled
  if (!isDevelopment() && !almIntegrationEnabled) {
    return <Navigate to="/" replace />;
  }

  const eventsByMonth = calendarEvents.reduce(
    (acc, event) => {
      const eventDate = new Date(event.dateString);
      const monthKey = eventDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }); // "Sep 2025"

      if (!acc[monthKey]) {
        acc[monthKey] = [];
      }

      acc[monthKey].push(event);

      return acc;
    },
    {} as Record<string, typeof calendarEvents>,
  );

  return (
    <div css={calendarContainerStyles}>
      <div css={calendarContentStyles}>
        {Object.entries(eventsByMonth).map(([monthKey, events]) => (
          <div key={monthKey}>
            <div css={calendarHeaderStyles}>
              <Heading level={3}>
                {new Date(events[0].dateString).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
              </Heading>
            </div>
            <div css={eventsListStyles}>
              {events.map((event) => (
                <ContentOverviewCard
                  key={event.id}
                  heading={event.title}
                  subheading={`${event.dateString} at ${event.timeRange}`}
                  tags={[
                    { label: event.typeLabel },
                    ...(event.location ? [{ label: `Location: ${event.location}` }] : []),
                    ...(event.instructor ? [{ label: `Instructor: ${event.instructor}` }] : []),
                  ]}
                  state={
                    event.status === 'not_started'
                      ? ContentOverviewCardState.DEFAULT
                      : ContentOverviewCardState.IN_PROGRESS
                  }
                  contentType={ContentType.COURSE}
                  onClickButton={() => {}}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const calendarContainerStyles = css({
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  minHeight: '100vh',
});

const calendarContentStyles = css({
  width: '100%',
  maxWidth: '1200px',
  padding: '32px',

  '@media (min-width: 1280px)': {
    marginTop: '100px',
  },
});

const calendarHeaderStyles = css({
  margin: '64px 0 48px 0',
  textAlign: 'left',
});

const eventsListStyles = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '16px',
  marginTop: '16px',

  '& [data-testid="toggle-visibility"]': {
    display: 'none',
  },
});
